import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { useSocketStore } from '../../stores/socket.store';
import toast from 'react-hot-toast';

type AgentStatus = 'available' | 'busy' | 'away';

interface StatusOption {
  value: AgentStatus;
  label: string;
  color: string;
  dotClass: string;
  /** Sent to the API */
  is_available: boolean;
  /** Human-readable status message stored on the member */
  message: string | null;
}

const STATUS_OPTIONS: StatusOption[] = [
  {
    value: 'available',
    label: 'Available',
    color: 'text-accent-2',
    dotClass: 'bg-accent-2',
    is_available: true,
    message: null,
  },
  {
    value: 'busy',
    label: 'Busy',
    color: 'text-accent-warn',
    dotClass: 'bg-accent-warn',
    is_available: false,
    message: 'Busy',
  },
  {
    value: 'away',
    label: 'Away',
    color: 'text-text-3',
    dotClass: 'bg-text-3',
    is_available: false,
    message: 'Away',
  },
];

interface StatusBarProps {
  collapsed: boolean;
}

export default function StatusBar({ collapsed }: StatusBarProps) {
  const [status, setStatus] = useState<AgentStatus>('available');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const events = useSocketStore((s) => s.events);

  const current = STATUS_OPTIONS.find((s) => s.value === status)!;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // No-op: listen for own status echoed back via socket from other tabs
  useEffect(() => {
    void events;
  }, [events]);

  // Persist status change to API
  const statusMutation = useMutation({
    mutationFn: (opt: StatusOption) =>
      api.patch('/api/agents/me/status', {
        is_available: opt.is_available,
        status_message: opt.message,
      }),
    onError: () => {
      toast.error('Failed to update status');
      // Revert optimistic update
      setStatus('available');
    },
  });

  function handleSelect(opt: StatusOption) {
    setStatus(opt.value); // optimistic
    setOpen(false);
    statusMutation.mutate(opt);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors',
          'hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          collapsed && 'justify-center px-0',
          statusMutation.isPending && 'opacity-60 cursor-wait',
        )}
        title={collapsed ? current.label : undefined}
        disabled={statusMutation.isPending}
      >
        <span
          className={cn(
            'shrink-0 w-2.5 h-2.5 rounded-full shadow-sm transition-colors',
            current.dotClass,
            status === 'available' && 'shadow-accent-2/50',
          )}
        />
        {!collapsed && (
          <span className={cn('text-sm font-medium truncate', current.color)}>
            {current.label}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute bottom-full mb-1 left-0 z-50 w-44',
            'bg-surface border border-border rounded-xl shadow-2xl p-1',
            collapsed && 'left-12',
          )}
        >
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt)}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
                'hover:bg-surface-2',
                status === opt.value ? opt.color + ' font-medium' : 'text-text-2',
              )}
            >
              <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', opt.dotClass)} />
              {opt.label}
              {status === opt.value && (
                <span className="ml-auto text-xs text-text-3">active</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
